import { Fragment, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import CompletePreferencesForm from '~/panels/student/components/CompletePreferencesForm'
import PanelContactCard from '~/panels/components/PanelContactCard'
import PanelNextSession from '~/panels/components/PanelNextSession'
import SettingsFormProvider, { UserSettingsFields } from '~/contexts/SettingsFormContext'
import StudentTutors from '~/panels/components/StudentTutors/StudentTutors'
import StudentPackages from '~/panels/components/StudentPackages'
import { LevelOfEducation, Subject, SubjectItem } from '~/types/booking'
import { constructPhoneNumberWithCallingCode, getBrowserTimezone, isValidPhoneNumber } from '~/utils/helpers'
import { generateTutorBookingLabels } from '~/panels/utils/panels.utils'
import { useEditStudentMutation, useGetProfileQuery, useGetStudentExistsQuery } from '~/redux/apis/student-api'
import useCurrentUser from '~/hooks/useCurrentUser'
import { administrationPhoneNumber } from '~/shared/shared.constants'
import { Student } from '~/types/tutoring-api'
import { useAppSelector } from '~/redux/hooks'
import { isAuthenticated } from '~/auth/slices/auth.slice'

function logStudentMissingFields(student: Student): void {
  const fields = ['totalHours', 'completedHours', 'noShowcancelledHours', 'lateCancelledHours']

  const missingFields = fields.filter((field: keyof Student) => student[field] == null)

  if (missingFields.length > 0) {
    console.error('Student profile endpoint did not return the following required:', student.userId, missingFields)
  }
}

export default function StudentMyTutor(): React.ReactElement {
  const { t } = useTranslation(['studentPanel', 'generics'])
  const user = useCurrentUser()
  const authenticated = useAppSelector(isAuthenticated)
  const { data: isStudentExists } = useGetStudentExistsQuery(undefined, { skip: !authenticated })
  const { data: student } = useGetProfileQuery(undefined, { skip: !isStudentExists })
  const [editStudent] = useEditStudentMutation()

  const router = useRouter()
  const timezone = getBrowserTimezone()

  useEffect(() => {
    if (student) {
      logStudentMissingFields(student)
    }
  }, [student])

  if (!student) {
    return <Fragment />
  }

  const {
    bookings = [],
    mentors: tutors = [],
    nextSession,
    totalHours = 0,
    completedHours = 0,
    noShowcancelledHours = 0, // TODO: There is a typo here, should be noShowCancelledHours.
    lateCancelledHours = 0,
    preference,
  } = student

  const shouldDisplayCompletePreferencesForm =
    tutors.length === 0 &&
    (!preference?.schoolCountry || preference?.timeslots?.length === 0 || preference?.traits?.length === 0)

  const hoursSpent = completedHours + noShowcancelledHours + lateCancelledHours
  const packages = generateTutorBookingLabels(bookings, t)

  const handleOnSubmit = async (fields: UserSettingsFields): Promise<void> => {
    const shouldAddMathGradeLevels =
      Array.isArray(fields.subjects.levelsOfEducation) &&
      fields.subjects.levelsOfEducation.includes(LevelOfEducation.GRADE_11_TO_13) &&
      fields.subjects.subjects.some((elem) => elem.name === Subject.MATH) &&
      fields.schoolCountry === 'Norway'

    const subjectsWithMathGradeLevels: SubjectItem[] = [
      ...fields.subjects.subjects.filter((elem) => elem.name !== Subject.MATH),
      { name: Subject.MATH, gradeLevels: fields.gradeLevels },
    ]
    const { countryCallingCode, phone, schoolCountry, traits, timeslots, subjects } = fields
    const phoneWithCallerCode = constructPhoneNumberWithCallingCode(countryCallingCode, phone)

    if (!isValidPhoneNumber(phoneWithCallerCode) || !schoolCountry) {
      return
    }

    await editStudent({
      studentId: user?.id,
      preference: {
        timeslots,
        traits,
        schoolCountry,
        subjects: {
          ...subjects,
          subjects: shouldAddMathGradeLevels ? subjectsWithMathGradeLevels : subjects.subjects,
        },
      },
      user: {
        fullName: fields.fullName,
        phone: phoneWithCallerCode,
        timezone,
      },
    })
    router.reload()
  }

  if (shouldDisplayCompletePreferencesForm) {
    return (
      <section className="w-full max-w-md space-y-4">
        <div className="p-4 space-y-4 rounded shadow-large bg-white">
          <div className="text-md font-semibold text-red">{t('studentPanel:completePreferencesHeader')}</div>
          <p className="text-gray-light-fade">{t('studentPanel:completePreferencesText')}</p>
          <SettingsFormProvider formType="my-tutor" onSubmit={handleOnSubmit} user={student}>
            <CompletePreferencesForm />
          </SettingsFormProvider>
        </div>

        <PanelContactCard
          email="learn@houseofmath.com"
          header={t('generics:administration')}
          isAlwaysColumn
          phoneNumber={administrationPhoneNumber}
        />
      </section>
    )
  }

  return (
    <div className="max-w-2xl space-y-4 md:space-y-0 md:space-x-4 md:space-x-reverse flex flex-col md:flex-row">
      <div className="min-w-0 flex-1 order-1 md:order-2 space-y-4 flex flex-col">
        <PanelNextSession nextSession={nextSession} />
        <StudentPackages
          header={t('studentPanel:myPackages')}
          hoursSpent={hoursSpent}
          packages={packages}
          totalHours={totalHours}
        />
        <PanelContactCard
          className="hidden md:block"
          email="learn@houseofmath.com"
          header={t('generics:administration')}
          isAlwaysColumn
          phoneNumber={administrationPhoneNumber}
        />
      </div>

      <div className="order-2 md:order-1 space-y-4">
        <StudentTutors tutors={tutors} />
      </div>

      <PanelContactCard
        className="order-3 md:hidden"
        email="learn@houseofmath.com"
        header={t('generics:administration')}
        isAlwaysColumn
        phoneNumber={administrationPhoneNumber}
      />
    </div>
  )
}
